import * as React from 'react';
import {useSelector} from 'react-redux';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Session} from 'nutshell-core/session';
import type {UserPermissions} from 'nutshell-core/users/users-types';

import {colors} from 'shells/colors/colors';
import {NavIconAdministrationIcon} from 'shells/icon/navigation/nav-icon-administration';
import {NavIconAutomationIcon} from 'shells/icon/navigation/nav-icon-automation';
import {NavIconConnectionsIcon} from 'shells/icon/navigation/nav-icon-connections';
import {NavIconDataIcon} from 'shells/icon/navigation/nav-icon-data';
import {NavIconOrganizationIcon} from 'shells/icon/navigation/nav-icon-organization';
import {NavIconWebsiteIcon} from 'shells/icon/navigation/nav-icon-website';
import {NavIconYourSettingsIcon} from 'shells/icon/navigation/nav-icon-your-settings';
import {KeyboardIcon} from 'shells/icon/svg/keyboard-icon';
import {ProductIcon} from 'shells/icon/svg/product-icon';
// @ts-expect-error - imported from .js/.jsx file
import {StepIcon} from 'shells/icon/svg/step-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {SecondarySidebar} from 'shells/navigation/secondary-sidebar/secondary-sidebar';
import type {NavItem} from 'shells/navigation/types';
import {Body} from 'shells/typography/body';

import {shouldShowConstantContact} from '../../../integrations/should-show-buttons';
import type {NutshellState} from '../../../store';

import {KeyboardShortcutsModal} from './keyboard-shortcuts/keyboard-shortcuts-modal';

import styles from './settings-nav.module.css';

/**
 * Helper function to take all of our admin link sections and concatenate
 * them into an array. It is memoized for performance reasons
 *
 * This includes feature flag filtering, otherwise it could be done
 * as a constant outside of this class.
 *
 * @return {Object[]}   - Array of link sections
 */
function getLinks(
    userPermissions?: UserPermissions | null,
    hideScrollableItems = false
): NavItem[] {
    if (!userPermissions) {
        return [];
    }

    if (!userPermissions.canAccessSetup) {
        // If a user does not have permissions to access setup, they could still have access to specific pages
        const links: any[] = [
            userPermissions.canAccessCrm
                ? {...YOUR_SETTINGS_SECTION, submenu: [...(YOUR_SETTINGS_SECTION.submenu || [])]}
                : {...YOUR_SETTINGS_SINGLE_ITEM},
        ];
        if (userPermissions.canAccessBilling) {
            links.push({
                id: 'adminBilling',
                label: 'Billing',
                icon: NavIconAdministrationIcon,
                to: '/setup/billing',
            });
        }

        const dataSection = {
            id: 'data',
            label: 'Data',
            icon: NavIconDataIcon,
            to: '/setup/import',
            submenu: [],
        } as const;

        if (userPermissions.canImport) {
            // @ts-expect-error
            dataSection.submenu.push({
                id: 'thirdPartyImport',
                label: 'Import',
                to: '/setup/import',
                activeMatch: /\/setup\/import.*$/,
                tags: ['lead', 'leads', 'person', 'people', 'company', 'companies'],
            });
        }

        // only put trash in data if we have import- otherwise, push it onto your settings.
        if (
            userPermissions.canDeleteEntities &&
            userPermissions.canImport &&
            userPermissions.canAccessCrm
        ) {
            // @ts-expect-error
            dataSection.submenu.push({
                id: 'trash',
                label: 'Trash',
                to: '/trash',
            });
        } else if (userPermissions.canDeleteEntities && userPermissions.canAccessCrm) {
            links[0].submenu.push({
                id: 'trash',
                label: 'Trash',
                to: '/trash',
            });
        }

        if (dataSection.submenu.length > 0) {
            links.push(dataSection);
        }

        return links;
    }

    const dataSection = getDataSection(userPermissions);
    const organizationSection = getOrganizationSection();
    const yourSettingsSection = userPermissions.canAccessCrm
        ? YOUR_SETTINGS_SECTION
        : YOUR_SETTINGS_SINGLE_ITEM;

    const defaultLinks = [
        yourSettingsSection,
        ADMINISTRATION_SECTION,
        dataSection,
        organizationSection,
        CONNECTIONS_SECTION,
        WEBSITE_SECTION,
    ];

    // Only add Sales automation and Products if not hiding scrollable items

    if (
        NutClientConfig.hasNewNavSettingsExternalLinks &&
        userPermissions.canAccessSetup &&
        userPermissions.canAccessCrm &&
        !hideScrollableItems
    ) {
        defaultLinks.push(SALES_AUTOMATION_LINK);
        defaultLinks.push(PRODUCTS_LINK);
    }

    const oldSalesProcessLinks = [
        yourSettingsSection,
        ADMINISTRATION_SECTION,
        PIPELINE_SECTION_OLD,
        dataSection,
        ORGANIZATION_SECTION,
        CONNECTIONS_SECTION,
    ];

    return NutClientConfig.hasSalesProcessSetupOverhaulNUT7581
        ? defaultLinks
        : oldSalesProcessLinks;
}

const YOUR_SETTINGS_SINGLE_ITEM: NavItem = {
    id: 'yourSettings',
    label: 'Your settings',
    icon: NavIconYourSettingsIcon,
    to: '/my-account/settings',
};

const YOUR_SETTINGS_SECTION: NavItem = {
    id: 'yourSettings',
    label: 'Your settings',
    icon: NavIconYourSettingsIcon,
    to: '/my-account/settings',
    hasDivider: true,
    submenu: [
        {
            id: 'profile',
            label: 'Profile',
            to: '/my-account/settings',
        },
        {
            id: 'phone',
            label: 'Phone',
            to: '/my-account/phone',
        },
        {
            id: 'calendar',
            label: 'Calendar',
            to: '/my-account/calendar',
        },
        {
            id: 'notifications',
            label: 'Notifications',
            to: '/my-account/notifications',
        },
        {
            id: 'email-digests',
            label: 'Email digests',
            to: '/my-account/email-digests',
            requiresUpgrade: !NutClientConfig.hasEmailDigests,
        },
        {
            id: 'tasks',
            label: 'Tasks',
            to: '/my-account/tasks',
        },
        {
            id: 'email-sending',
            label: 'Email send',
            to: '/my-account/email-sending',
        },
        {
            id: 'sync-email',
            label: 'Sync email',
            to: '/my-account/email-syncing',
        },
    ],
};

const ADMINISTRATION_SECTION: NavItem = {
    id: 'admin',
    label: 'Administration',
    icon: NavIconAdministrationIcon,
    to: '/setup/security',
    submenu: [
        {
            id: 'general',
            label: 'General',
            to: '/setup/index',
        },
    ],
};

if (ADMINISTRATION_SECTION.submenu) {
    ADMINISTRATION_SECTION.submenu.push({
        id: 'aiFeatures',
        label: 'AI features',
        to: '/setup/ai-features',
    });
}

if (ADMINISTRATION_SECTION.submenu) {
    ADMINISTRATION_SECTION.submenu.push(
        {
            id: 'security',
            label: 'Security',
            to: '/setup/security',
        },
        {
            id: 'billing',
            label: 'Billing',
            to: '/setup/billing',
            activeMatch: /\/setup\/billing/,
            // @ts-expect-error
            tags: ['invoice', 'invoices', 'credit', 'payment', 'users', 'plan'],
        },
        {
            id: 'users',
            label: 'Users & teams',
            to: '/setup/user',
            tags: ['visibility', 'permissions'],
        },
        {
            id: 'auditLog',
            label: 'Audit log',
            to: '/setup/audit-log',
            requiresUpgrade: !NutClientConfig.hasAuditLog,
        },
        {
            id: 'emailSecurity',
            label: 'Email security',
            to: '/setup/email-security',
        }
    );
}

const ADMINISTRATION_PAUSED_SECTION: NavItem = {
    id: 'admin',
    label: 'Administration',
    icon: NavIconAdministrationIcon,
    to: '/setup/billing/paused',
    submenu: [
        {
            id: 'billing',
            label: 'Reactivate Nutshell',
            to: '/setup/billing/paused',
            activeMatch: /\/setup\/billing/,
        },
        {
            id: 'users',
            label: 'Users & teams',
            to: '/setup/user',
        },
    ],
};

const ADMINISTRATION_DISABLED_SECTION: NavItem = {
    id: 'admin',
    label: 'Administration',
    icon: NavIconAdministrationIcon,
    to: '/setup/billing/expired',
    submenu: [
        {
            id: 'billing',
            label: 'Purchase Nutshell',
            to: '/setup/billing/expired',
            activeMatch: /\/setup\/billing/,
        },
        {
            id: 'users',
            label: 'Users & teams',
            to: '/setup/user',
        },
    ],
};

const WEBSITE_SECTION: NavItem = {
    id: 'website',
    label: 'Website',
    icon: NavIconWebsiteIcon,
    submenu: [
        {
            id: 'domains',
            label: 'Domains',
            to: '/setup/domains',
        },
        {
            id: 'trackingSnippet',
            label: 'Tracking snippet',
            to: '/setup/tracking-snippet',
        },
    ],
};

const ORGANIZATION_SECTION: NavItem = {
    id: 'organization',
    label: 'Organization',
    icon: NavIconOrganizationIcon,
    to: '/setup/activity-type',
    submenu: [
        {
            id: 'quotasActivityTypes',
            label: 'Activity types',
            to: '/setup/activity-type',
        },
        {
            id: 'orgSources',
            label: 'Channels & sources',
            to: '/setup/source',
            // @ts-expect-error
            tags: ['lead', 'leads'],
        },
        {
            id: 'orgCompanyTypes',
            label: 'Company types',
            to: '/setup/company-type',
            // @ts-expect-error
            tags: ['companies'],
        },
        {
            id: 'orgCompetitors',
            label: 'Competitors',
            to: '/setup/competitor',
            // @ts-expect-error
            tags: ['lead', 'leads', 'company', 'companies'],
        },
        {
            id: 'orgCustomFields',
            label: 'Custom fields',
            to: '/setup/custom-field',
            // @ts-expect-error
            tags: [
                'lead',
                'leads',
                'person',
                'people',
                'contact',
                'company',
                'companies',
                'account',
            ],
        },
        {
            id: 'orgIndustries',
            label: 'Industries',
            to: '/setup/industry',
            // @ts-expect-error
            tags: ['lead', 'leads'],
        },
        {
            id: 'orgMarkets',
            label: 'Markets',
            to: '/setup/market',
            // @ts-expect-error
            tags: ['currency'],
            requiresUpgrade: !NutClientConfig.hasMultipleMarkets,
        },
        {
            id: 'orgProducts',
            label: 'Products',
            to: '/setup/product',
            // @ts-expect-error
            tags: ['lead', 'leads'],
        },
        {
            id: 'orgTags',
            label: 'Tags',
            to: '/setup/tag',
            // @ts-expect-error
            tags: ['lead', 'leads', 'person', 'people', 'company', 'companies'],
        },
        {
            id: 'orgTerritories',
            label: 'Territories',
            to: '/setup/territory',
            requiresUpgrade: !NutClientConfig.hasTerritories,
        },
    ],
};

const pipelineSectionOld: NavItem = {
    id: 'pipeline',
    label: 'Sales process',
    icon: StepIcon,
    to: '/setup/stage',
    submenu: [
        {
            id: 'leadsPipelines',
            label: 'Pipelines & stages',
            to: '/setup/stage',
            // @ts-expect-error
            tags: ['lead', 'leads'],
        },
        {
            id: 'processProcesses',
            label: 'Processes',
            to: '/setup/process-builder',
            // @ts-expect-error
            tags: ['lead', 'leads'],
        },
        {
            id: 'processTriggers',
            label: 'Triggers',
            to: '/setup/distribution/process',
            activeMatch: /\/setup\/distribution\/(?:process|overlay)$/,
            // @ts-expect-error
            tags: ['lead', 'leads'],
        },
        {
            id: 'processDelays',
            label: 'Delays',
            to: '/setup/delay',
            // @ts-expect-error
            tags: ['lead', 'leads'],
        },
        {
            id: 'leadsAssignment',
            label: 'User assignment',
            to: '/setup/distribution/user',
            activeMatch: /\/setup\/distribution\/user$/,
            // @ts-expect-error
            tags: ['lead', 'leads'],
        },
    ],
};

if (NutshellFrontendSettings.usingNewDelays === true && pipelineSectionOld.submenu) {
    // Remove the delays page if we're using the new delays
    pipelineSectionOld.submenu = pipelineSectionOld.submenu.filter((item) => {
        return item.id !== 'processDelays';
    });
}

const PIPELINE_SECTION_OLD = pipelineSectionOld;

const DATA_SECTION: NavItem = {
    id: 'data',
    label: 'Data',
    icon: NavIconDataIcon,
    to: '/setup/import',
    submenu: [
        {
            id: 'thirdPartyImport',
            label: 'Import',
            to: '/setup/import',
            activeMatch: /\/setup\/import.*$/,
            // @ts-expect-error
            tags: ['lead', 'leads', 'person', 'people', 'company', 'companies'],
        },
        {
            id: 'adminExport',
            label: 'Export',
            to: '/setup/export',
        },
        {
            id: 'integrations',
            label: 'Integrations',
            to: '/setup/integration',
        },
        {
            id: 'peopleIq',
            label: 'PeopleIQ',
            to: '/setup/people-iq',
            // @ts-expect-error
            isNew: true,
        },
        {
            id: 'sqlAccess',
            label: 'SQL access',
            to: '/setup/sql-access',
            requiresUpgrade: !NutClientConfig.hasSqlAccess,
        },
    ],
};

const getDataSection = (userPermissions: UserPermissions): NavItem => {
    const dataSection = {
        ...DATA_SECTION,
        submenu: [...(DATA_SECTION.submenu || [])],
    } as const;
    if (userPermissions.canDeleteEntities) {
        // @ts-expect-error
        dataSection.submenu.push({
            id: 'trash',
            label: 'Trash',
            to: '/trash',
        });
    }

    // @ts-expect-error
    return dataSection;
};

const getOrganizationSection = (): NavItem => {
    const organizationSection = {
        ...ORGANIZATION_SECTION,
        submenu: [...(ORGANIZATION_SECTION.submenu || [])],
    } as const;

    // @ts-expect-error
    organizationSection.submenu = organizationSection.submenu.filter((item) => {
        return item.id !== 'orgProducts';
    });

    // @ts-expect-error
    return organizationSection;
};

const CONNECTIONS_SECTION: NavItem = {
    id: 'connections',
    label: 'Connections',
    icon: NavIconConnectionsIcon,
    to: '/setup/api-key',
    submenu: [
        {
            id: 'connectionsApi',
            label: 'API keys',
            to: '/setup/api-key',
        },
        {
            id: 'connectionsMobile',
            label: 'Mobile devices',
            to: '/setup/mobile',
        },
    ],
};

if (shouldShowConstantContact(NutClientConfig.integrations) && CONNECTIONS_SECTION.submenu) {
    CONNECTIONS_SECTION.submenu.push({
        id: 'connectionsConstantContact',
        label: 'Constant Contact',
        to: '/setup/constant-contact',
    });
}

if (NutClientConfig.hasUnbounce && CONNECTIONS_SECTION.submenu) {
    CONNECTIONS_SECTION.submenu.push({
        id: 'connectionsUnbounce',
        label: 'Unbounce',
        to: '/setup/unbounce',
    });
}

const SALES_AUTOMATION_LINK: NavItem = {
    id: 'salesAutomation',
    label: 'Sales automation',
    to: '/sales/pipelines',
    isExternalLink: true,
    icon: NavIconAutomationIcon,
};

const PRODUCTS_LINK: NavItem = {
    id: 'orgProducts',
    label: 'Products',
    to: '/sales/product',
    isExternalLink: true,
    icon: ProductIcon,
};

const PAUSED_LINKS = [ADMINISTRATION_PAUSED_SECTION];
const DISABLED_LINKS = [ADMINISTRATION_DISABLED_SECTION];

type OwnProps = {
    paused?: boolean;
    disabled?: boolean;
};

type RoutedProps = OwnProps & ContextRouter;
type Props = RoutedProps;

function SettingsNavComponent(props: Props) {
    const [isKeyboardShortcutsModalOpen, setIsKeyboardShortcutsModalOpen] = React.useState(false);
    const [hideScrollableItems, setHideScrollableItems] = React.useState(false);

    const userPermissions = useSelector((state: NutshellState) =>
        Session.getUserPermissions(state)
    );

    // Auto-open keyboard shortcuts modal via ?shortcuts=true (from command palette)
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('shortcuts') === 'true') {
            setIsKeyboardShortcutsModalOpen(true);
            // Clear the param so refresh doesn't re-trigger
            params.delete('shortcuts');
            const newSearch = params.toString();
            const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
            props.history.replace(newUrl);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const checkWindowHeight = () => {
            setHideScrollableItems(window.innerHeight < 850);
        };

        // Check initially
        checkWindowHeight();

        // Check on window resize
        window.addEventListener('resize', checkWindowHeight);

        return () => {
            window.removeEventListener('resize', checkWindowHeight);
        };
    }, []);

    let navItems = getLinks(userPermissions, hideScrollableItems);
    if (props.paused) {
        navItems = PAUSED_LINKS;
    } else if (props.disabled) {
        navItems = DISABLED_LINKS;
    } else if (!userPermissions) {
        navItems = [];
    }

    // Hide keyboard shortcuts footer if hideScrollableItems is true
    const footer = !hideScrollableItems && (
        <button
            onClick={() => setIsKeyboardShortcutsModalOpen(true)}
            className={styles['shortcuts-button']}
        >
            <StackLegacy isHorizontal={true} alignCenter={true} spacing={4}>
                <Body size='small' nowrap={true} color='white'>
                    Keyboard shortcuts
                </Body>
                {/* @ts-expect-error */}
                <KeyboardIcon size={21} alignCenter={true} color={colors.white} />
            </StackLegacy>
        </button>
    );

    return (
        <>
            <SecondarySidebar navItems={navItems} footer={footer} />
            <KeyboardShortcutsModal
                isOpen={isKeyboardShortcutsModalOpen}
                onClose={() => setIsKeyboardShortcutsModalOpen(false)}
            />
        </>
    );
}

// @ts-expect-error
export const SettingsNavNew = withRouter<OwnProps>(SettingsNavComponent);
